import type { ScheduleOverrideWithRelations } from '@snootsvet/shared'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  ScheduleOverrideForm,
  type ScheduleOverrideFormValues,
} from '@/frontend/features/schedules/schedule-override.form'

/**
 * What the form dialog is doing: adding a new shift with optional prefilled values,
 * or editing an existing one. `null` keeps the dialog closed.
 */
export type ScheduleOverrideFormState =
  | { mode: 'add'; defaults: ScheduleOverrideFormValues }
  | { mode: 'edit'; override: ScheduleOverrideWithRelations }
  | null

interface Props {
  state: ScheduleOverrideFormState
  onClose: () => void
  /** Invoked when the user picks Delete from the edit form (hands off to the confirm dialog). */
  onDelete: (override: ScheduleOverrideWithRelations) => void
}

/** Add/Edit dialog wrapping the shared ScheduleOverrideForm. */
export function ScheduleOverrideFormDialog({ state, onClose, onDelete }: Props) {
  return (
    <Dialog open={state !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{state?.mode === 'edit' ? 'Edit shift' : 'Add one-off shift'}</DialogTitle>
        </DialogHeader>
        {state?.mode === 'edit' ? (
          <ScheduleOverrideForm
            // Remount per override so the form re-seeds its fields when switching rows.
            key={state.override.id}
            overrideId={state.override.id}
            defaultValues={{
              staffId: state.override.staffId,
              date: state.override.date,
              clinicId: state.override.clinicId,
              templateId: state.override.templateId,
              note: state.override.note ?? '',
            }}
            onSuccess={onClose}
            onCancel={onClose}
            onDelete={() => onDelete(state.override)}
          />
        ) : state ? (
          <ScheduleOverrideForm
            defaultValues={state.defaults}
            onSuccess={onClose}
            onCancel={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
